import { useTranslation } from '@/store/languageStore';

export type KeyPresenceKind = 'in_home' | 'in_other' | 'taken' | 'unknown';

export interface VehicleKeyPresence {
  linked: boolean;
  keyName?: string | null;
  keyNumber?: string | null;
  presence: KeyPresenceKind;
  homeCabinetName?: string | null;
  currentCabinetName?: string | null;
  slot?: number | null;
}

interface Props {
  presence: VehicleKeyPresence | null;
  registrationNumber: string;
}

const badgeClass: Record<KeyPresenceKind, string> = {
  taken: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  in_home: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  in_other: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  unknown: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

export default function VehicleKeyTagStatus({ presence, registrationNumber }: Props) {
  const { t } = useTranslation();

  if (!presence || !presence.linked) {
    return <p className="text-textSecondary text-xs">{t('vehicleCard.booking.keyTagNone')}</p>;
  }

  const label = (() => {
    switch (presence.presence) {
      case 'taken':
        return t('vehicleCard.booking.keyTagTaken');
      case 'in_home':
        return t('vehicleCard.booking.keyTagInHome');
      case 'in_other':
        return t('vehicleCard.booking.keyTagInOther');
      case 'unknown':
        return t('vehicleCard.booking.keyTagUnknown');
      default: {
        const _exhaustive: never = presence.presence;
        return _exhaustive;
      }
    }
  })();

  return (
    <div className="space-y-2 text-textDefault">
      <div className="font-medium">{presence.keyName || registrationNumber}</div>
      {presence.keyNumber ? (
        <div className="text-textTertiary text-xs font-mono">{presence.keyNumber}</div>
      ) : null}
      <div>
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass[presence.presence]}`}>
          {label}
        </span>
      </div>
      {presence.presence === 'in_home' && presence.homeCabinetName ? (
        <p className="text-textSecondary text-xs">
          {presence.homeCabinetName}
          {presence.slot != null ? ` · ${t('vehicleCard.booking.keyTagSlot', { slot: presence.slot })}` : ''}
        </p>
      ) : null}
      {presence.presence === 'in_other' ? (
        <>
          <p className="text-textSecondary text-xs">
            {presence.currentCabinetName || '—'}
            {presence.slot != null ? ` · ${t('vehicleCard.booking.keyTagSlot', { slot: presence.slot })}` : ''}
          </p>
          {presence.homeCabinetName ? (
            <p className="text-textTertiary text-xs">
              {t('vehicleCard.booking.keyTagHomeCabinet', { name: presence.homeCabinetName })}
            </p>
          ) : null}
          <p className="text-textTertiary text-xs">{t('vehicleCard.booking.keyTagOtherAllowed')}</p>
        </>
      ) : null}
      {presence.presence === 'taken' && presence.homeCabinetName ? (
        <p className="text-textTertiary text-xs">
          {t('vehicleCard.booking.keyTagHomeCabinet', { name: presence.homeCabinetName })}
        </p>
      ) : null}
    </div>
  );
}
